import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import DonationModal from "../components/DonationModal";
import Header from "../components/Header";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

const getProxiedImageUrl = (url) => {
  if (!url) return "";
  if (url.includes("supabase.co")) {
    return `${API_URL}/creators/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

export default function Dashboard() {
  const [stats, setStats] = useState({ total_views: 0, published_videos: 0, helpful_rating: 0 });
  const [recentVideos, setRecentVideos] = useState([]);
  const [creator, setCreator] = useState({ name: "", profile_image: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showDonate, setShowDonate] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const init = async () => {
      if (!token) {
        navigate("/login");
        return;
      }
      await Promise.all([fetchCreator(), fetchStats()]);
      setLoading(false);
    };
    init();
  }, []);

  const fetchCreator = async () => {
    try {
      const res = await axios.get(`${API_URL}/creators/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreator(res.data);
      localStorage.setItem("creator", JSON.stringify(res.data));
    } catch (err) {
      console.error("Fetch creator error:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      setError(true);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/videos/my-videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const videos = res.data;
      const totalViews = videos.reduce((sum, v) => sum + (v.views || 0), 0);
      const published = videos.filter(v => v.status === "published").length;
      setStats({ total_views: totalViews, published_videos: published, helpful_rating: 4.5 });
      setRecentVideos(videos.slice(0, 5));
    } catch (err) {
      console.error("Fetch stats error:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }
      setError(true);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Failed to load dashboard data.</p>
        <button onClick={() => window.location.reload()} className="bg-primary text-white px-4 py-2 rounded-md">Retry</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />

      <main className="container mx-auto px-4 py-8">
        {/* Quick Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Link to="/submit" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition">
            <div className="text-2xl font-bold text-primary">+ Upload Video</div>
            <div className="text-gray-500 text-sm">Share your explanation</div>
          </Link>
          <Link to="/available-requests" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition">
            <div className="text-2xl font-bold text-primary">📋 View Requests</div>
            <div className="text-gray-500 text-sm">Claim verses people need</div>
          </Link>
          <Link to="/my-claims" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition">
            <div className="text-2xl font-bold text-primary">🎬 My Claims</div>
            <div className="text-gray-500 text-sm">Track your claimed verses</div>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-primary">{stats.published_videos}</div>
            <div className="text-gray-500 mt-1">Published Videos</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-primary">{stats.total_views}</div>
            <div className="text-gray-500 mt-1">Total Views</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-3xl font-bold text-primary">{stats.helpful_rating}</div>
            <div className="text-gray-500 mt-1">Average Rating</div>
          </div>
        </div>

        {/* Creator Guidelines */}
        <div className="bg-blue-50 border-l-4 border-primary rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-primary mb-4">Creator Guidelines & Limits</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800">Video Specifications</h3>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-2 space-y-1">
                <li>Max file size: 500 MB</li>
                <li>Recommended format: MP4</li>
                <li>Keep videos concise (3–10 minutes)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Upload Limits</h3>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-2 space-y-1">
                <li>Weekly limit: 5 videos</li>
                <li>Pending limit: 3 videos (until reviewed)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Content Quality</h3>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-2 space-y-1">
                <li>Clear, respectful, theologically sound</li>
                <li>Focus on context and original meaning</li>
                <li>No speculation or misinformation</li>
              </ul>
            </div>
          </div>
          <p className="text-sm text-green-700 mt-4">Videos meeting these guidelines are published faster and help users grow in faith.</p>
        </div>

        {/* Donation Button */}
        <div className="flex justify-center mb-8">
          <button onClick={() => setShowDonate(true)} className="bg-primary text-white px-6 py-2 rounded-full shadow hover:bg-opacity-90 transition">
            Support VerSeer
          </button>
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-primary mb-4">Recent Submissions</h2>
          {recentVideos.length === 0 ? (
            <p className="text-gray-500">No videos yet. <Link to="/submit" className="text-primary hover:underline">Submit your first video</Link></p>
          ) : (
            <div className="divide-y">
              {recentVideos.map(video => (
                <div key={video.id} className="py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <div className="font-medium">{video.verse}</div>
                    <div className="text-sm text-gray-500">Submitted: {new Date(video.created_at).toLocaleDateString()}</div>
                    {video.status === "published" && <div className="text-sm text-gray-500">{video.views || 0} views</div>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    video.status === "published" ? "bg-green-100 text-green-800" :
                    video.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                    "bg-red-100 text-red-800"
                  }`}>
                    {video.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <DonationModal visible={showDonate} onClose={() => setShowDonate(false)} />
    </div>
  );
}